import { ASSET_CLASS, DISTPERIODS, FIELDS, FOOTNOTE_DISCLAIMERS, FOOTNOTE_DISCLAIMERS_NUMBERS } from '../../shared/screener-constants';
import footnoteDisclaimers from '../../statics/screener/footnote-disclaimers';
import expenseDisclosures from '../../statics/screener/expense-disclosures';
import frequentTraderDisclosures from '../../statics/screener/frequent-trader-disclosures';
import distDisclaimers from '../../statics/screener/distributions-footnote-disclaimers';
import _uniq from 'lodash/uniq';
import { FUND_TYPES } from '../../stores/profile/get-fund-type';
import moment from 'moment-msdate';
import format from '../format';
import { shouldShowHypoChart } from '../sharedHelpers';

const footnoteLabelDisclaimerHelper = footnotesLabel => {
  // Always next year if todays date is March 1 or later.
  const today = moment();
  const februaryEndThisYear = today.clone().month(1).endOf('month');
  const februaryEndNextYear = today.clone().add(1, 'year').month(1).endOf('month');
  const februaryEnd = today.isBefore(moment().startOf('year').add(2, 'months').date(1)) ? februaryEndThisYear : februaryEndNextYear;
  const februaryEndDate = februaryEnd.format('MMMM DD YYYY');

  const TIAATraditionalCurrentRates = `<p>Funds applied to TIAA Traditional from ${footnotesLabel} will
                                      be credited with the indicated rates until ${februaryEndDate}.</p>`;
  return TIAATraditionalCurrentRates;
};


const getDisclaimerObj = (textStr, supNum = 0) => {
  return {
    number: supNum,
    text: textStr
  };
};

/*Numbered disclaimers increase the supNum by 1 each time a numbered disclaimer is created */
const createNumberDisclaimer = (row, numberedDisclaimers, disclaimerKey, disclaimer, superScriptNumKey, superScriptNum) => {
  if (!numberedDisclaimers[disclaimerKey]) {
    numberedDisclaimers[disclaimerKey] = getDisclaimerObj(disclaimer, superScriptNum);
    row[superScriptNumKey] = superScriptNum;
    superScriptNum++;
  } else {
    row[superScriptNumKey] = numberedDisclaimers[disclaimerKey].number;
  }
  return superScriptNum;
};

const getHypotheticalChartDisclaimer = (row, supNum, numberedDisclaimers) => {
  if (shouldShowHypoChart(row[FIELDS.TCFUNDTYPE])) {
    supNum = createNumberDisclaimer(
      row,
      numberedDisclaimers,
      FOOTNOTE_DISCLAIMERS.HYPOTHETICALCHART,
      footnoteDisclaimers.hypotheticalChart,
      FOOTNOTE_DISCLAIMERS_NUMBERS.HYPOTHETICALCHARTISNUM,
      supNum
    );
  }
  return supNum;
};

const getTIAATraditionalNumberedDisclaimers = (row, supNum, numberedDisclaimers, footnotesLabel) => {
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaa) { //this is TIAA traditional !!
    supNum = createNumberDisclaimer(
      row,
      numberedDisclaimers,
      FOOTNOTE_DISCLAIMERS.AVGANNUALTIAATRADITIONAL,
      footnoteDisclaimers.avgAnnualTIAATraditional,
      FOOTNOTE_DISCLAIMERS_NUMBERS.AVEANNUALDISNUM,
      supNum
    );

    if (row[FIELDS.ASSETCLASS] === ASSET_CLASS.GUARANTEED) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.ASSETCLASSGUARENTEEDTIAATRADITIONAL,
        footnoteDisclaimers.AssetClassGuarenteedTIAATraditional,
        FOOTNOTE_DISCLAIMERS_NUMBERS.ASSETCLASSDISNUM,
        supNum
      );
    }

    if (row.hasOwnProperty(FIELDS.TCCURRENTRATE)) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.TIAATRADITIONALCURRENTRATES,
        footnoteLabelDisclaimerHelper(footnotesLabel.footnotesLabel),
        FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTRATEDISNUM,
        supNum
      );
    }
  }
  return supNum;
};

const getNonTIAAFixedAnnuityNumberedDisclaimers = (row, supNum, numberedDisclaimers) => {
  if (row[FIELDS.TCFUNDTYPE] !== FUND_TYPES.fixedAnnuityTiaa) {
    supNum = createNumberDisclaimer(
      row,
      numberedDisclaimers,
      FOOTNOTE_DISCLAIMERS.AVGANNUALNONTIAATRADITIONAL,
      footnoteDisclaimers.avgAnnualNonTIAATraditional,
      FOOTNOTE_DISCLAIMERS_NUMBERS.AVEANNUALDISNUM,
      supNum
    );
    if (row[FIELDS.ASSETCLASS] === ASSET_CLASS.GUARANTEED) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.ASSETCLASSGUARENTEEDNONTRADITIONAL,
        footnoteDisclaimers.AssetClassGuarenteedNonTraditional,
        FOOTNOTE_DISCLAIMERS_NUMBERS.ASSETCLASSDISNUM,
        supNum
      );
    }
  }
  return supNum;
};

const getTIAAStableValueNumberedDisclaimers = (row, supNum, numberedDisclaimers) => {
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaaStableValue) {
    supNum = createNumberDisclaimer(
      row,
      numberedDisclaimers,
      FOOTNOTE_DISCLAIMERS.TIAASTABLEVALUENUMBERED,
      footnoteDisclaimers.TIAAStableValueNumbered,
      FOOTNOTE_DISCLAIMERS_NUMBERS.PRODUCTTYPEDISNUM,
      supNum
    );
  }
  return supNum;
};

const getNumberedDisclaimers = (data, footnotesLabel) => {
  let supNum = 1;
  const numberedDisclaimers = {};
  data.forEach(row => {
    supNum = getTIAATraditionalNumberedDisclaimers(row, supNum, numberedDisclaimers, footnotesLabel);
    supNum = getNonTIAAFixedAnnuityNumberedDisclaimers(row, supNum, numberedDisclaimers);
    supNum = getTIAAStableValueNumberedDisclaimers(row, supNum, numberedDisclaimers);
    supNum = getHypotheticalChartDisclaimer(row, supNum, numberedDisclaimers);
    supNum = createNumberDisclaimer(
      row,
      numberedDisclaimers,
      FOOTNOTE_DISCLAIMERS.MSTARNUMBERED,
      footnoteDisclaimers.MStarNumbered,
      FOOTNOTE_DISCLAIMERS_NUMBERS.MSTARDISNUM,
      supNum
    );

    if (row.hasOwnProperty(FIELDS.TCFUNDNAV)) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.MUTUALFUNDNAV,
        footnoteDisclaimers.mutualFundNav,
        FOOTNOTE_DISCLAIMERS_NUMBERS.NAVDISNUM,
        supNum
      );
    }

    if (row.hasOwnProperty(FIELDS.TCSECYIELD)) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.TIAAACCESSVARIABLEANNUITYMONEYMARKET30DAYSECYIELD,
        footnoteDisclaimers.TIAAAccessVariableAnnuityMoneyMarket30DaySECYield,
        FOOTNOTE_DISCLAIMERS_NUMBERS.SECYEILDDISNUM,
        supNum
      );
    }

    if (row[FIELDS.ASSETCLASS] === ASSET_CLASS.MONEYMARKET) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.MUTUALFUND7DAYCURRENTEFFECTIVEYIELD,
        footnoteDisclaimers.mutualFund7DayCurrentEffectiveYield,
        FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTEFFECTIVEYIELDDISNUM,
        supNum
      );
    }

    if (row.hasOwnProperty(FIELDS.TCCURRENTAPY)) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.FDICPRODUCTANNUALPERCENTAGEYIELD,
        footnoteDisclaimers.FDICProductAnnualPercentageYield,
        FOOTNOTE_DISCLAIMERS_NUMBERS.APYDISNUM,
        supNum
      );
    }

    if (row.hasOwnProperty(FIELDS.TCCURRENTINTERESTRATE)) {
      supNum = createNumberDisclaimer(
        row,
        numberedDisclaimers,
        FOOTNOTE_DISCLAIMERS.FDICPRODUCTCURRENTINTERESTRATE,
        footnoteDisclaimers.FDICProductCurrentInterestRate,
        FOOTNOTE_DISCLAIMERS_NUMBERS.CURRENTINTERESTRATEDISNUM,
        supNum
      );
    }
  });
  return numberedDisclaimers;
};

const checkForLinkedPerformance = row => {
  return (
    row[FIELDS.TCISLINKEDPERF1YR] ||
    row[FIELDS.TCISLINKEDPERF3YR] ||
    row[FIELDS.TCISLINKEDPERF5YR] ||
    row[FIELDS.TCISLINKEDPERF10YR]
  );
};

const getCollapsibleDisclaimers = (row, collapsibleDisclaimers) => {
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaa) { //this is TIAA traditional !!
    collapsibleDisclaimers.push(footnoteDisclaimers.TIAATraditionalAlways);
  }
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityCref) {
    collapsibleDisclaimers.push(footnoteDisclaimers.CREFVariableAnnuityAlways);
  }
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityTiaaAccess) {
    collapsibleDisclaimers.push(footnoteDisclaimers.TIAAAccessVariableAnnuityAlways);
  }
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaaStableValue) {
    collapsibleDisclaimers.push(footnoteDisclaimers.TIAAStableValueAlways);
  }
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.retirePlus) {
    collapsibleDisclaimers.push(footnoteDisclaimers.RetirePlusModelsAlways);
  }
  if (row[FIELDS.TCFUNDTYPE] === FUND_TYPES.customPortfolioModel) {
    collapsibleDisclaimers.push(footnoteDisclaimers.customPortfolioModelsAlways);
  }
  if (checkForLinkedPerformance(row)) {
    collapsibleDisclaimers.push(footnoteDisclaimers.hypotheticalPerformance);
  }
  return collapsibleDisclaimers;
};

export const getExpenseDisclosure = (row, nonPropMFCapNote) => {

  const fundType = row[FIELDS.TCFUNDTYPE];
  const isProp = row[FIELDS.TCTREATASPROP] > 0;
  const expirationDate = format.oaDate(row[FIELDS.TCEXPIRATIONDATE]);

  let disclosure = '';
  if (!isProp) {
    if (fundType === FUND_TYPES.mutualFund) {
      disclosure = nonPropMFCapNote;
    }
    return disclosure;
  }

  switch (fundType) {
    case FUND_TYPES.retirePlus:
      disclosure = expenseDisclosures.expenseRatioRetirePlus;
      break;
    case FUND_TYPES.customPortfolioModel:
    case FUND_TYPES.retirePlusPro:
      disclosure = expenseDisclosures.expenseRatioRetirePlusPro;
      break;
    case FUND_TYPES.mutualFund:
      disclosure = expenseDisclosures.propMutualFundsCapNote(expirationDate);
      break;
    case FUND_TYPES.variableAnnuityTiaaAccess:
      disclosure = expenseDisclosures.tiaaAccessCapNote(expirationDate);
      break;
    case FUND_TYPES.variableAnnuityCref:
    case FUND_TYPES.variableAnnuityTiaa:
      disclosure = expenseDisclosures.crefVAExpenseNote;
      break;
    default:
      disclosure = '';
  }
  return disclosure;
};


export const getNetGrossExpenseDisclosure = (row, nonPropMFCapNote) => {

  const isProp = row[FIELDS.TCTREATASPROP] > 0;
  const expirationDate = format.oaDate(row[FIELDS.TCEXPIRATIONDATE]);

  let disclosure = '';
  if (!isProp) {
    disclosure = nonPropMFCapNote;
    return disclosure;
  }
  disclosure = expenseDisclosures.propMutualFundsCapNote(expirationDate);
  return disclosure;
};

export const getDistributionsDisclaimers = ({
  projectedDistributionAsOfDate,
  annualDistributionAsOfDate,
  distPeriod
}) => {
  let distributionDisclaimers = [];

  if (distPeriod === DISTPERIODS.YEAR) {
    const year = format.oaDate(annualDistributionAsOfDate, { formatString: 'YYYY' });
    const numberedDisclaimers = [
      distDisclaimers.distributionNotice(year),
      distDisclaimers.shortLongGainsDisclosure
    ];
    distributionDisclaimers = [...numberedDisclaimers];
  }

  if (distPeriod === DISTPERIODS.PROJECTED) {
    const date = format.oaDate(projectedDistributionAsOfDate, { formatString: 'MMMM Do YYYY' });
    distributionDisclaimers.push(distDisclaimers.seasonalEstimateDisclosure(date));
  }

  distributionDisclaimers = [
    ...distributionDisclaimers,
    distDisclaimers.regBIDisclosure,
    distDisclaimers.taxInfoDisplayLast
  ];

  return distributionDisclaimers;
};

export const getFootnotesDisclaimers = (data, footnotesLabel) => {
  let hasMutualFundProp = false;
  let hasFixedAnnuityTiaa = false;
  let hasVariableAnnuityCref = false;
  let hasVariableAnnuityCrefMM = false;
  let hasVariableAnnuityTiaaAccess = false;
  let hasVariableAnnuityTiaaAccessMM = false;
  let hasFixedAnnuityTiaaStableValue = false;
  let hasRetirePlus = false;
  let hasCustomPortfolioModel = false;
  let hasLinkedPerformance = false;
  let hasMutualFundMMProp = false;
  let hasMutualFundMMNonProp = false;

  const getNoncollapsibleDisclaimers = noncollapsibleDisclaimers => {
    // console.log('non collapsible ', row[FIELDS.TCFUNDTYPE])
    noncollapsibleDisclaimers.push(footnoteDisclaimers.defaultNoncollapsible);
    noncollapsibleDisclaimers.push(footnoteDisclaimers.defaultNoncollapsiblePrint);
    if (hasCustomPortfolioModel) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.defaultNoncollapsibleModelPrint);
    }
    if (hasMutualFundProp) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.salesChargeDisclosure);
    }
    noncollapsibleDisclaimers.push(footnoteDisclaimers.EducationalPurposesOnly);
    if (hasVariableAnnuityTiaaAccess) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.TIAAAccessVariableAnnuityAlways);
    }
    if (hasVariableAnnuityTiaaAccessMM) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.TIAAAccessVariableAnnuityMoneyMarket);
    }
    if (hasVariableAnnuityCrefMM) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.CREFVariableAnnuityMoneyMarket);
    }
    if (hasMutualFundMMProp) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.mutualFundMoneyMaketProp);
    }
    if (hasMutualFundMMNonProp) {
      noncollapsibleDisclaimers.push(footnoteDisclaimers.mutualFundMoneyMaketNonProp);
    }
    return noncollapsibleDisclaimers;
  };

  const defaultCollapsibleAboveNumbered = [];
  let collapsibleDisclaimers = [];
  let nonCollapsibleDisclaimers = [];
  const numberedDisclaimers = getNumberedDisclaimers(data, footnotesLabel);

  data.forEach(row => {
    collapsibleDisclaimers = getCollapsibleDisclaimers(row, collapsibleDisclaimers);
  });

  const checkConditions = [
    {update: row => {if (!hasMutualFundProp) hasMutualFundProp = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.mutualFund && row[FIELDS.TCTREATASPROP] === 1}},
    {update: row => {if (!hasFixedAnnuityTiaa) hasFixedAnnuityTiaa = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaa}},
    {update: row => {if (!hasVariableAnnuityCref) hasVariableAnnuityCref = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityCref}},
    {update: row => {if (!hasVariableAnnuityCrefMM) hasVariableAnnuityCrefMM = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityCref && row[FIELDS.ASSETCLASS] === 'Money Market'}},
    {update: row => {if (!hasVariableAnnuityTiaaAccess) hasVariableAnnuityTiaaAccess = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityTiaaAccess}},
    {update: row => {if (!hasVariableAnnuityTiaaAccessMM) hasVariableAnnuityTiaaAccessMM = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.variableAnnuityTiaaAccess && row[FIELDS.ASSETCLASS] === 'Money Market'}},
    {update: row => {if (!hasFixedAnnuityTiaaStableValue) hasFixedAnnuityTiaaStableValue = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.fixedAnnuityTiaaStableValue}},
    {update: row => {if (!hasRetirePlus) hasRetirePlus = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.retirePlus}},
    {update: row => {if (!hasCustomPortfolioModel) hasCustomPortfolioModel = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.customPortfolioModel}},
    {update: row => {if (!hasLinkedPerformance) hasLinkedPerformance = checkForLinkedPerformance(row)}},
    {update: row => {if (!hasMutualFundMMProp) hasMutualFundMMProp = hasMutualFundProp && row[FIELDS.ASSETCLASS] === 'Money Market'}},
    {update: row => {if (!hasMutualFundMMNonProp) hasMutualFundMMNonProp = row[FIELDS.TCFUNDTYPE] === FUND_TYPES.mutualFund && row[FIELDS.TCTREATASPROP] === 0 && row[FIELDS.ASSETCLASS] === 'Money Market'}}
  ]

  data.forEach(row => {
    checkConditions.forEach(condition => {
      condition.update(row);
    })
  });

  nonCollapsibleDisclaimers = getNoncollapsibleDisclaimers(nonCollapsibleDisclaimers);

  defaultCollapsibleAboveNumbered.push(footnoteDisclaimers.defaultAboveNumbered);

  collapsibleDisclaimers.push(footnoteDisclaimers.defaultBelowNumbered);
  collapsibleDisclaimers = _uniq(collapsibleDisclaimers);
  nonCollapsibleDisclaimers = _uniq(nonCollapsibleDisclaimers);

  return { defaultCollapsibleAboveNumbered, collapsibleDisclaimers, nonCollapsibleDisclaimers, numberedDisclaimers };
};

export const getFrequentTraderDisclosuresFund = row => {
  const fundType = row[FIELDS.TCFUNDTYPE];
  const transferLimitCode = row[FIELDS.TCTRANSFERLIMITTYPECODE];
  const numberOfDays = row[FIELDS.TCNUMBEROFDAYS];
  const daysBlocked = row[FIELDS.TCDAYSBLOCKED];

  if (fundType === FUND_TYPES.variableAnnuityTiaa) {
    return frequentTraderDisclosures.fund.tiaaVariableAnnuityFTD;
  }

  if (fundType === FUND_TYPES.variableAnnuityCref
    && transferLimitCode === 100001
    && format.isValid(numberOfDays)
    && format.isValid(daysBlocked)
  ) {
    return frequentTraderDisclosures.fund.getCrefVariableAnnuityFTD(numberOfDays, daysBlocked);
  }

  if (
    fundType === FUND_TYPES.mutualFund
    && format.isValid(transferLimitCode)
    && format.isValid(numberOfDays)
    && format.isValid(daysBlocked)
  ) {
    return frequentTraderDisclosures.fund.getMutualFundFTD(numberOfDays);
  }

  if (fundType === FUND_TYPES.mutualFund && !format.isValid(transferLimitCode)) {
    return frequentTraderDisclosures.fund.mutualFundFTD;
  }

  return '';
};

const getPlanFTDRestrictedHoldings = (row, holdingsByContext, propertyKey) => {
  const validHoldings = [];
  holdingsByContext.forEach(item => item.holdings?.forEach(holding => {
    if (
      row[FIELDS.TCWSODISSUE] === holding.xid
      && holding.hasOwnProperty(propertyKey)
      && (holding[propertyKey] !== 'Y')
    ) {
      validHoldings.push(holding);
    }
  }));
  return validHoldings;
};

export const getFrequentTraderDisclosuresPlan = (row, holdingsByContext) => {
  // No plan? No plan-specific FTDs!
  if (holdingsByContext.length === 0) return '';

  const contributionsDisallowed = getPlanFTDRestrictedHoldings(row, holdingsByContext, 'contribution');
  const transfersDisallowed = getPlanFTDRestrictedHoldings(row, holdingsByContext, 'transfer');

  // Multiple plan logic
  if (holdingsByContext.length > 1) {
    if (contributionsDisallowed.length > 0 && transfersDisallowed.length > 0) {
      const contributionDisallowedPlansFTD = frequentTraderDisclosures.plan.getContributionDisallowedPlansFTD(contributionsDisallowed);
      const transferDisallowedPlansFTD = frequentTraderDisclosures.plan.getTransferDisallowedPlansFTD(transfersDisallowed);

      return `${contributionDisallowedPlansFTD}<br><br>${transferDisallowedPlansFTD}`;
    }

    if (contributionsDisallowed.length > 0) {
      return frequentTraderDisclosures.plan.getContributionDisallowedPlansFTD(contributionsDisallowed);
    }

    if (transfersDisallowed.length > 0) {
      return frequentTraderDisclosures.plan.getTransferDisallowedPlansFTD(transfersDisallowed);
    }
  }

  // Single plan logic
  if (contributionsDisallowed.length > 0 && transfersDisallowed.length > 0) {
    return frequentTraderDisclosures.plan.contributionAndTransferDisallowedPlanFTD;
  }

  if (contributionsDisallowed.length > 0) {
    return frequentTraderDisclosures.plan.contributionDisallowedPlanFTD;
  }

  if (transfersDisallowed.length > 0) {
    return frequentTraderDisclosures.plan.transfersDisallowedPlanFTD;
  }

  return '';
};

export const removeDisclaimer = (string, array) => {
  const arrCopy = [...array];
  const index = arrCopy.findIndex(disclaimer => disclaimer.includes(string));
  arrCopy.splice(index, 1);
  return arrCopy;
};
